import React, { useEffect, useMemo, useState } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useNavigate, useParams } from 'react-router-dom';
import { NavigationHeader } from '../../../../../components/NavigationHeader';
import { CHOOSE_METHOD_TYPES, STYLES } from '../consts';
import { ReactComponent as BankIcon } from '../../../../../assets/icons/svg/shared/ic-shared-bank.svg';
import { ReactComponent as BackIcon } from '../../../../../assets/svg/left-arrow-title.svg';
import Button from '../../../../../generic-components/Button';
import Heading from '../../../../../generic-components/Heading';
import AppLayout from '../../../../../generic-components/Layout';
import { APP_LAYOUT_STYLES } from '../../../../../generic-components/Layout/constants';
import { convertValueToUSD } from '../../../../../helpers/currency.helper';
import { useAccountSummaryQuery } from '../../../../../hooks/query/useAccountSummaryQuery';
import { VIEWS_MAP } from '../../../../../router/constants';
import { trackMixPanel } from '../../../../../services/mixpanel.service';
import { useStore } from '../../../../../store';
import { PaymentErrorDisclaimer } from '../../CreateBill/components/PaymentErrorDisclaimer';
import StripActionList from '../components/StripActionList';
import { useFetchInitialData } from '../hooks/useFetchInitialData';
import { useFetchVendorById } from '../hooks/useFetchVendorById';
import { PaymentMethods } from '../../../types';
import { MoveMoneyTypes } from '../../../../MoveMoney/consts';
import Spinner, { SpinnerColor } from '../../../../../generic-components/Spinner';
import { validateFeatureFlag } from '../../../../../services/featureFlagService';
import { FEATURE_FLAG } from '../../../../../constants/general';
import { isVendorAchDataValid } from '../../../../MoveMoney/utils';

const PayBill = () => {
  const { t } = useTranslation();
  const { customerId } = useStore((state) => state.user);
  const routerHistory = useStore((state) => state.routerHistory);
  const setMoveMoneyData = useStore((state) => state.setMoveMoneyData);
  const moveMoneyData = useStore((state) => state.moveMoneyData);
  const { accountExternalId } = useStore((state) => state.getPersonalAccount());
  const { balance } = useStore((state) => state.getPrimaryAccount());
  const { billId } = useParams();
  const navigate = useNavigate();
  const [shouldShowPaymentError, setShouldShowPaymentError] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string>('');

  /** Queries */
  const { data: initialData, isLoading: isInitialLoadingState } = useFetchInitialData({
    customerId,
    accountExternalId,
    billId: billId || '',
    onError: (error) => {
      setShouldShowPaymentError(true);
      setErrMsg(error);
    },
  });
  const { refetch: getSummaryData, isRefetching, isLoading } = useAccountSummaryQuery();
  const {
    refetch: fetchVendorData,
    data: vendorData,
    isRefetching: isLoadingInitialData,
  } = useFetchVendorById({
    customerId,
    accountExternalId,
    supplierExternalId: initialData?.billData?.supplierId || '',
  });

  useEffect(() => {
    if (initialData?.billData.supplierId) {
      fetchVendorData();
    }
  }, [initialData]);

  const handleStripPick = async (methodType: string) => {
    switch (methodType) {
      case CHOOSE_METHOD_TYPES.ACH:
        trackMixPanel('Act on bill pay methods', { Action: 'Bank transfer ACH' });
        setMoveMoneyData({
          ...moveMoneyData,
          billId: initialData?.billData.billId || '',
          amount: initialData?.billData.amount || 0,
          vendorData: vendorData || undefined,
        });
        navigate(`/${VIEWS_MAP.MOVE_MONEY.BASE}/${MoveMoneyTypes.ACH_OUT_TRANSFER}`);
        break;

      case CHOOSE_METHOD_TYPES.CHECK:
        trackMixPanel('Act on bill pay methods', { Action: 'Mail a Check' });
        setMoveMoneyData({
          ...moveMoneyData,
          billId: initialData?.billData.billId || '',
          amount: initialData?.billData.amount || 0,
          vendorData: vendorData || undefined,
        });
        navigate(`/${VIEWS_MAP.MOVE_MONEY.BASE}/${MoveMoneyTypes.MAIL_CHECK}`);
        break;

      case CHOOSE_METHOD_TYPES.WIRE:
        trackMixPanel('Act on bill pay methods', { Action: 'Wire Transfer' });
        setMoveMoneyData({
          ...moveMoneyData,
          billId: initialData?.billData.billId || '',
          amount: initialData?.billData.amount || 0,
          vendorData: vendorData || undefined,
        });
        navigate(`/${VIEWS_MAP.MOVE_MONEY.BASE}/${MoveMoneyTypes.WIRE_OUT}`);
        break;
      case CHOOSE_METHOD_TYPES.INTERNATIONAL_WIRE:
        trackMixPanel('Act on bill pay methods', { Action: 'International Wire Transfer' });
        setMoveMoneyData({
          ...moveMoneyData,
          billId: initialData?.billData.billId || '',
          amount: initialData?.billData.amount || 0,
          vendorData: vendorData || undefined,
        });
        navigate(`/${VIEWS_MAP.MOVE_MONEY.BASE}/${MoveMoneyTypes.INTERNATIONAL_WIRE_OUT}`);
        break;

      case CHOOSE_METHOD_TYPES.ASK_VENDOR:
        trackMixPanel('Act on bill pay methods', { Action: 'Ask Your Vendor' });
        setMoveMoneyData({
          ...moveMoneyData,
          billId: initialData?.billData.billId || '',
          amount: initialData?.billData.amount || 0,
          vendorData: vendorData || undefined,
        });
        navigate(`/${VIEWS_MAP.BILLS.BASE}/${VIEWS_MAP.BILLS.ASK_VENDOR.BASE}`);
        break;

      default: {
        break;
      }
    }
  };

  const renderInitialLoadingState = () => (
    <div className='flex flex-col items-center justify-center mt-[50px]'>
      <Skeleton className='mb-[12px]' count={5} width='400px' height='54px' />
    </div>
  );

  const shouldShowWireTransferDisclaimer = useMemo(
    () => initialData?.paymentMethods.methods.some((method) => method.methodType === PaymentMethods.WIRE),
    [initialData?.paymentMethods.methods],
  );

  const isShowAskVendorDetails = useMemo(
    () =>
      validateFeatureFlag(FEATURE_FLAG.ASK_VENDOR_FOR_DETAILS) &&
      vendorData &&
      !isVendorAchDataValid(vendorData) &&
      initialData &&
      initialData.billData.isSupportRequestVendorDetails,
    [initialData, vendorData],
  );
  useEffect(() => {
    trackMixPanel('View bill pay methods', { Origin: routerHistory[routerHistory.length - 2] || 'Home' });
    getSummaryData();
  }, []);
  return (
    <AppLayout
      layoutStyle={APP_LAYOUT_STYLES.CHOOSE_PAYMENT_METHOD.MAIN_LAYOUT_CHOOSE_METHOD}
      navBarStyle={`${APP_LAYOUT_STYLES.CHOOSE_PAYMENT_METHOD.NAVBAR_LAYOUT} gap-0`}
      hideCustomerProfile
      chatBotStyle='hidden'
    >
      <div className={STYLES.WRAPPER}>
        {shouldShowPaymentError && (
          <PaymentErrorDisclaimer
            message={errMsg}
            amount={initialData?.billData.amount}
            onClosePopUp={() => navigate(`/${VIEWS_MAP.BILLS.BASE}`)}
          />
        )}
        <NavigationHeader
          list={[{ name: 'Bill' }, { name: 'New Bill' }, { name: 'Pay' }]}
          onCloseAction={() => navigate(`/${VIEWS_MAP.BILLS.BASE}`)}
          pageTitle='Pay a Bill'
          layoutStyle={STYLES.NAVIGATION_HEADER}
        />
        {(isInitialLoadingState || isLoading || isRefetching) || !vendorData || !initialData?.billData?.amount ? (
          renderInitialLoadingState()
        ) : (
          <div className='text-center'>
            <div className='relative'>
              <Button
                className='flex items-center gap-[4px] absolute left-0 top-0 translate-x-[-200px] translate-y-[10px]'
                id='backToBillsHubCta'
                onClick={() => navigate(`/${VIEWS_MAP.BILLS.BASE}`)}
                childElement={
                  <>
                    <BackIcon />
                    <Heading title='Back' className='font-metricR border-b border-b-bw08 text-[16px] text-bw08' />
                  </>
                }
              />
              <Heading className={STYLES.MAIN_HEADING} title={t('ACH_OUT_CHOOSE_METHOD.TITLE')} />
            </div>

            <CurrencyInput
              readOnly
              id='inputAmountToTransfer'
              name='input-name'
              className={`${STYLES.INPUT} border-none text-bw08 !w-[230px]`}
              defaultValue={initialData?.billData?.amount || 0}
              decimalsLimit={2}
              prefix='$'
            />
            <div className={`${STYLES.ACCOUNT_DETAILS} mb-[32px]`}>
              <div className={STYLES.FLEX_WRAPPER}>
                <BankIcon className='mr-[5px]' />
                <Heading className={STYLES.CHECKING_ACCOUNT} title={t('ACH_OUT_CHOOSE_METHOD.CHECKING_ACCOUNT')} />
              </div>
              <div>
                <Heading className={STYLES.AVAILABLE_BALANCE} title={t('ACH_OUT_CHOOSE_METHOD.AVAILABLE_BALANCE')} />
                <Heading className={STYLES.AVAILABLE_BALANCE_VALUE} title={convertValueToUSD(balance, 2)} />
              </div>
            </div>
            {!isLoadingInitialData && vendorData ? (
              <StripActionList
                paymentMethods={initialData?.paymentMethods}
                onStripClick={handleStripPick}
                isShowAskVendorDetails={isShowAskVendorDetails || false}
              />
            ) : (
              <Spinner height='2rem' width='2rem' spinnerColor={SpinnerColor.Blue} />
            )}
            {shouldShowWireTransferDisclaimer && (
              <Heading className={STYLES.DISCLAIMER} title={t('ACH_OUT_CHOOSE_METHOD.DISCLAIMER')} />
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default PayBill;
